import * as React from "react";
import Link from "next/link";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ExternalLink } from "lucide-react";

import { cn } from "@/ui/cn";

const linkVariants = cva(
  "inline-flex items-center gap-2 transition-colors outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "text-primary-text hover:text-primary-text hover:brightness-110 active:text-primary-text/90",
        secondary:
          "text-secondary-foreground hover:text-secondary-foreground hover:brightness-110 active:text-secondary-foreground/90",
        destructive:
          "text-destructive-text hover:text-destructive-text hover:brightness-110 active:text-destructive-text/90",
        muted: "text-muted-foreground hover:text-foreground active:text-muted-foreground",
        ghost: "hover:text-foreground hover:brightness-110",
        anchor:
          "border-b border-dashed border-muted-foreground hover:border-foreground hover:text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

type LinkProps = Omit<React.ComponentPropsWithoutRef<typeof Link>, "href"> &
  VariantProps<typeof linkVariants> & {
    href: string;
    asChild?: boolean;
  };

const LinkComponent = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, variant, href, children, asChild = false, ...props }, ref) => {
    const isExternal =
      href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:");

    if (asChild) {
      return (
        <Slot
          className={cn(linkVariants({ variant }), className)}
          ref={ref}
          draggable={false}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    if (isExternal) {
      return (
        <a
          href={href}
          className={cn(linkVariants({ variant }), className)}
          ref={ref}
          target="_blank"
          rel="noopener noreferrer"
          draggable={false}
          {...props}
        >
          {children}
          <ExternalLink className="size-3" aria-hidden="true" />
        </a>
      );
    }

    return (
      <Link
        href={href}
        className={cn(linkVariants({ variant }), className)}
        ref={ref}
        draggable={false}
        {...props}
      >
        {children}
      </Link>
    );
  }
);
LinkComponent.displayName = "Link";

export { LinkComponent as Link };
